// @ts-check
import React, { useState, useEffect } from 'react'
import { InputText } from '../../../ui'
import { useTranslation } from 'react-i18next'
import { useStore } from '../../../StateProvider'
import {
  MAX_LENGTH,
  VALIDATE_EMAIL_REGEXP
} from '../../../constants/constants'
import { map } from 'ramda'
import styles from './style.css'

const SameOriginError = () => {
  const { t } = useTranslation()
  return (
    <div className={styles.error}>{t('createAccount.emailAlreadyExistsWithResend1')}
      <a href='#resendForm'>{t('createAccount.emailAlreadyExistsWithResend2')}</a>
      {t('createAccount.emailAlreadyExistsWithResend3')}
    </div>)
}

/**
 * @param {{
 * i18nLabel: string,
 * type: string,
 * name: string,
 * maxlength?: number,
 * additionalInfo?: string,
 * required: boolean,
 * order?: number
 * }} props
 */
const ExternalEmailField = ({ i18nLabel, type, name, maxlength }) => {
  const { t } = useTranslation()
  const label = i18nLabel ? t(`emailField.${i18nLabel}`) : ''
  const [value, setValue] = useState('')
  const [isValid, setIsValid] = useState(null)
  const [isFilled, setIsFilled] = useState(false)
  const [error, setError] = useState(null)
  const [{
    whitelabel: { redirectToTier2FormAfterCreate },
    form,
    form: { accId },
    user: { email }
  }, dispatch] = useStore()
  const { alreadyExistsSameError, alreadyExistsDiffError } = form[accId]

  useEffect(() => {
    if (!email) return
    setValue(email)
    const isValid = getIsValid(name, email)
    setIsValid(isValid)
    setIsFilled(true)
    dispatch({ type: 'setExtFieldValue', name, value: email })
    dispatch({ type: 'setValidationFld', name, value: isValid })
  }, [email, name])

  useEffect(() => {
    if (alreadyExistsSameError) {
      setIsValid(false)
      setError(redirectToTier2FormAfterCreate
        ? 'createAccount.emailAlreadyExists'
        : <SameOriginError />)
      dispatch({ type: 'setValidationFld', name, value: false })
      dispatch({ type: 'setIsResendOpen', value: true })
    }
  }, [alreadyExistsSameError, redirectToTier2FormAfterCreate])

  /**
   * @param {React.ChangeEvent<HTMLInputElement>} e
   */
  const onChange = ({ target: { value } }) => {
    setValue(value)
    !value.trim() ? setIsFilled(false) : setIsFilled(true)

    if (alreadyExistsSameError || alreadyExistsDiffError) {
      dispatch({ type: 'setExtFieldValue', name: 'alreadyExistsSameError', value: false })
      dispatch({ type: 'setExtFieldValue', name: 'alreadyExistsDiffError', value: false })
    }
  }

  /**
   * @param {React.FocusEvent<HTMLInputElement>} e
   */
  const onBlur = ({ target: { name, value } }) => {
    const trimmedValue = value.trim()
    const isValid = getIsValid(name, trimmedValue)

    if (isValid) {
      setIsValid(true)
      setError('')
      dispatch({ type: 'setExtFieldValue', name, value })
    } else {
      setIsValid(false)
      setError(getErrorMsg(value))
    }

    dispatch({ type: 'setValidationFld', name, value: isValid })
  }

  /**
   * @param {string} name
   * @param {string} value
   * @returns {boolean}
   */
  const getIsValid = (name, value) =>
    alreadyExistsSameError ? false : VALIDATE_EMAIL_REGEXP.test(value)

  const getErrorMsg = (value) => {
    if (!isFilled) return ''
    if (alreadyExistsSameError) {
      return redirectToTier2FormAfterCreate ? 'createAccount.emailAlreadyExists' : <SameOriginError />
    } else {
      return VALIDATE_EMAIL_REGEXP.test(value) ? '' : 'emailField.invalid'
    }
  }

  const onKeyDown = (e) => {
    if (e.key === ' ') {
      e.preventDefault()
    }
  }

  return (
    <InputText
      name={name}
      label={label}
      value={value}
      type={type}
      key={name}
      maxLength={maxlength || MAX_LENGTH}
      error={typeof error === 'string' ? t(error) : error}
      onChange={onChange}
      onBlur={onBlur}
      valid={isValid}
      disabled={false}
      onKeyDown={onKeyDown}
    />
  )
}

const getExternalEmailFields = map(textField => {
  const { i18n, name, required, type, order, maxLength } = textField
  return (
    <ExternalEmailField
      i18nLabel={i18n}
      name={name}
      required={required}
      type={type}
      key={name}
      order={order}
      maxlength={maxLength}
    />
  )
})

export { ExternalEmailField, getExternalEmailFields }
